import type { CSSProperties } from 'react';

import { cn } from '@/lib/utils';

function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="skeleton"
			className={cn('bg-accent animate-pulse rounded-md', className)}
			{...props}
		/>
	);
}

/**
 * A placeholder cover and caption, for a home that would otherwise show
 * "Loading…" text: it reserves the cover's box so the real one fills it
 * instead of pushing the page down when it arrives.
 *
 * `aspectRatio` sits on a plain wrapper, not on the pulsing `Skeleton`:
 * an animating element with an aspect ratio was re-sized on every frame of
 * the pulse (see poets' magazines.tsx).
 */
function SkeletonCover({ width, maxWidth, aspectRatio, className }: SkeletonCoverProps) {
	const box: CSSProperties = width
		? { width, aspectRatio }
		: { width: '100%', maxWidth, aspectRatio };
	return (
		<div className={cn('shrink-0', className)} aria-hidden="true">
			<div style={box}>
				<Skeleton className="w-full h-full" />
			</div>
			<Skeleton className="mt-2 h-4 w-2/3" />
		</div>
	);
}

interface SkeletonCoverProps {
	/** A fixed width, for covers in a horizontal row. */
	width?: number;
	/** A fluid width capped here, for a single hero cover. */
	maxWidth?: number;
	/** The real cover's ratio, e.g. `'3 / 4'`. */
	aspectRatio: string;
	className?: string;
}

/**
 * A horizontal row of placeholder covers, laid out like the reader family's
 * issue and feed rows (`flex space-x-10 ... p-4 pt-0`, each item `p-3`).
 * Clipped rather than scrollable: there is nothing to scroll to yet.
 */
function SkeletonCoverRow({
	count = 6,
	width,
	aspectRatio,
	className,
}: SkeletonCoverRowProps) {
	return (
		<div
			className={cn('flex space-x-10 overflow-hidden p-4 pt-0', className)}
			aria-busy="true"
		>
			{Array.from({ length: count }).map((_, i) => (
				<SkeletonCover
					key={i}
					width={width}
					aspectRatio={aspectRatio}
					className="m-0 p-3"
				/>
			))}
		</div>
	);
}

interface SkeletonCoverRowProps {
	count?: number;
	width: number;
	aspectRatio: string;
	className?: string;
}

/** A 1x1 transparent GIF: the image draws nothing but the skeleton's fill. */
const TRANSPARENT_PIXEL =
	'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * A placeholder cover that is an `<img>`, so it takes the size the template's
 * own stylesheet gives its covers (`.issue img`, `.latest-issue figure img`
 * and their breakpoint overrides) instead of a second set of skeleton sizes
 * that would drift from them. `width`/`height` declare the 3:4 cover ratio,
 * as the real covers' attributes do, for a template whose rules set only a
 * width.
 */
function SkeletonCoverImage({ className }: { className?: string }) {
	return (
		<img
			src={TRANSPARENT_PIXEL}
			alt=""
			aria-hidden="true"
			width={340}
			height={453}
			data-slot="skeleton"
			className={cn('bg-accent animate-pulse rounded-md', className)}
		/>
	);
}

export { Skeleton, SkeletonCover, SkeletonCoverImage, SkeletonCoverRow };
